// Parse user message for trade intent
// Returns detected trade action if user wants to trade, null otherwise

export interface TradeIntent {
  action: 'buy' | 'sell' | 'swap'
  token: string | null  // null if couldn't parse specific token
  fromToken: string | null // for swaps: the token being sold
  amount: number | null // null if user didn't specify
  rawMessage: string
}

// Common trade keywords
const BUY_KEYWORDS = ['buy', 'ape', 'ape in', 'ape into', 'long', 'get some', 'grab', 'scoop', 'snag', 'acquire', 'purchase', 'invest in', 'go long']
const SELL_KEYWORDS = ['sell', 'dump', 'exit', 'close', 'short', 'get rid of', 'unload', 'liquidate', 'cash out', 'take profit', 'tp']
const SWAP_KEYWORDS = ['swap', 'trade', 'convert', 'exchange', 'flip']

// All tradeable tokens (must match the list in prompts.ts)
const TRADEABLE_TOKENS = [
  // Blue Chips
  'SOL', 'JUP', 'RAY', 'PYTH', 'JTO', 'RNDR', 'HNT', 'W',
  // Mid Caps
  'BONK', 'WIF', 'PENGU', 'ORCA', 'MNGO', 'MOBILE', 'SAMO', 'DRIFT', 'RENDER', 'MSOL', 'BSOL',
  // Degen
  'FARTCOIN', 'GOAT', 'POPCAT', 'MEW', 'MOTHER', 'ORE', 'TUNA', 'SLERF', 'BOME', 'WEN', 'MYRO', 'PONKE', 'MOODENG', 'PNUT', 'GIGA', 'TRUMP', 'AI16Z', 'ZEREBRO', 'GRIFFAIN', 'ACT', 'CHILLGUY', 'LOCKIN', 'DADDY', 'BRETT', 'SPX', 'TREMP', 'COST',
  // Ultra Degen
  'VINE', 'WOJAK', 'RETARDIO', 'SIGMA', 'BODEN', 'FWOG', 'LUIGI', 'HARRY', 'BUTTHOLE', 'HAWK', 'CATWIF', 'GME', 'USA', 'PEPE', 'DOGE', 'SHIB', 'CWIF', 'MICHI', 'NEIRO', 'GOATSEUS',
  // Stables
  'USDC', 'USDT',
]

// Find all tokens mentioned in a message, in order of appearance
function findTokensInMessage(message: string): string[] {
  const upperMessage = message.toUpperCase()
  const found: { token: string; index: number }[] = []

  for (const token of TRADEABLE_TOKENS) {
    const regex = new RegExp('\\b' + token + '\\b', 'i')
    const match = regex.exec(upperMessage)
    if (match) {
      found.push({ token, index: match.index })
    }
  }

  // Sort by position in message
  found.sort((a, b) => a.index - b.index)
  return found.map(f => f.token)
}

export function parseTradeIntent(message: string): TradeIntent | null {
  const lowerMessage = message.toLowerCase().trim()

  // Check for swap intent first (e.g., "swap SOL for RNDR", "trade SOL into RNDR")
  let isSwap = false
  for (const keyword of SWAP_KEYWORDS) {
    if (lowerMessage.includes(keyword)) {
      isSwap = true
      break
    }
  }

  // Detect swap pattern: two tokens mentioned with swap keyword
  if (isSwap) {
    const tokens = findTokensInMessage(message)
    if (tokens.length >= 2) {
      const fromToken = tokens[0]!
      const toToken = tokens[1]!

      // Don't swap stables to stables
      if (fromToken !== toToken) {
        // Try to find amount
        let amount: number | null = null
        const amountMatch = message.match(/\$?([\d,]+(?:\.\d+)?)\s*(?:k|K|usd|USD|dollars)?/i)
        if (amountMatch && amountMatch[1]) {
          amount = parseFloat(amountMatch[1].replace(/,/g, ''))
          if (message.toLowerCase().includes('k')) {
            amount *= 1000
          }
        }
        if (/\ball\b|\beverything\b|\bmax\b/i.test(message)) {
          amount = -1
        }

        return {
          action: 'swap',
          token: toToken,      // token being bought
          fromToken: fromToken, // token being sold
          amount,
          rawMessage: message,
        }
      }
    }
  }

  // Check for buy intent
  let isBuy = false
  let isSell = false

  for (const keyword of BUY_KEYWORDS) {
    if (lowerMessage.includes(keyword)) {
      isBuy = true
      break
    }
  }

  for (const keyword of SELL_KEYWORDS) {
    if (lowerMessage.includes(keyword)) {
      isSell = true
      break
    }
  }

  // If both or neither, check for more specific patterns
  if (isBuy && isSell) {
    // Ambiguous - look for more context
    // "sell X to buy Y" -> treat as sell first
    if (lowerMessage.indexOf('sell') < lowerMessage.indexOf('buy')) {
      isBuy = false
    } else {
      isSell = false
    }
  }

  if (!isBuy && !isSell) {
    return null
  }

  // Try to find token mentioned
  const tokens = findTokensInMessage(message)
  const foundToken = tokens.length > 0 ? tokens[0]! : null

  // Try to find amount
  let amount: number | null = null
  const amountMatch = message.match(/\$?([\d,]+(?:\.\d+)?)\s*(?:k|K|usd|USD|dollars)?/i)
  if (amountMatch && amountMatch[1]) {
    amount = parseFloat(amountMatch[1].replace(/,/g, ''))
    if (message.toLowerCase().includes('k')) {
      amount *= 1000
    }
  }

  // Check for "all" or "everything"
  if (/\ball\b|\beverything\b|\bmax\b/i.test(message)) {
    amount = -1 // Special value meaning "all"
  }

  return {
    action: isBuy ? 'buy' : 'sell',
    token: foundToken,
    fromToken: null,
    amount,
    rawMessage: message,
  }
}

// Check if user's message is purely a trade request vs a narrative action that mentions trading
export function isDirectTradeRequest(message: string): boolean {
  const lowerMessage = message.toLowerCase().trim()

  // Must mention a recognized tradeable token - otherwise it's a narrative action
  // e.g. "buy some beers" -> no token -> narrative, "buy SOL" -> token found -> trade
  const tokens = findTokensInMessage(message)
  if (tokens.length === 0) return false

  // Short messages with trade keywords are likely direct trade requests
  if (message.length < 50) {
    const intent = parseTradeIntent(message)
    if (intent) return true
  }

  // Longer messages might be narrative ("I walk to the terminal and consider buying...")
  // Only treat as direct trade if it's imperative/commanding
  const imperativePatterns = [
    /^(buy|sell|dump|ape|trade|swap)/i,
    /^(i want to|i'd like to|let me|gonna|going to) (buy|sell|dump|ape|trade|swap)/i,
    /^(execute|place|make) .*(buy|sell|order|trade)/i,
  ]

  for (const pattern of imperativePatterns) {
    if (pattern.test(lowerMessage)) {
      return true
    }
  }

  return false
}
